import {observable, action, computed} from 'mobx'
import {message} from 'antd'
import deepEqual from 'deep-equal'
import {mergeDeep} from 'Utils'
import {PasswordChange} from './passwordChange'
import {notification} from 'antd'
import * as Sentry from '@sentry/browser'

class Data {
  @observable email = ''
  @observable first_name = ''
  @observable last_name = ''
  @observable phone = ''
  @observable note = ''
  @observable date_joined = ''
  @observable is_professional_trader
  @observable instruments2 = []

  @observable is_active
  @observable is_email_verified
  @observable notifications = []
  @observable settings = {}
  @observable account = {}

  @observable is_hijacked
  @observable is_staff
  @observable is_superuser

  @computed get billing_plan() {
    if (this.account && this.account.billing_plan) {
      return this.account.billing_plan
    }
    return {}
  }
}

export class UserStore {
  @observable data = new Data()
  @observable dataToUpdate = new Data()
  @observable checkingLogin = true
  @observable loading = false
  @observable validationErrors = {}
  @observable emailVerificationFailed = false
  @observable emailResent = false
  @observable selectedPlan

  @observable hijack_email

  constructor(rootStore, api) {
    this.root = rootStore
    this.api = api
    this.passwordChange = new PasswordChange(rootStore, api)
  }

  @computed get changedPlan() {
    return this.selectedPlan !== this.data.billing_plan.id
  }

  @computed get changed() {
    return !deepEqual(this.data, this.dataToUpdate)
  }

  @computed get planSettings() {
    const {account, billing_plan} = this.data
    if (typeof billing_plan !== 'object') {
      return [{}]
    }
    let obj = Object.assign({}, billing_plan)
    for (let name in obj) {
      if (account[`custom_${name}`] !== null && account[`custom_${name}`] !== undefined) {
        obj[name] = account[`custom_${name}`]
      }
    }
    return [obj]
  }

  @action setHijackEmail = (value) => {
    this.hijack_email = value
  }

  @action selectPlan = (id) => {
    this.selectedPlan = id
  }

  @action resetEmailConfirmationStatus = () => {
    this.emailVerificationFailed = false
    this.emailResent = false
  }

  @action changeField = (name, value) => {
    this.dataToUpdate[name] = value
  }

  @action changeNotification = (value) => {
    let notifications = Array.isArray(this.dataToUpdate.account.notifications) ? this.dataToUpdate.account.notifications.concat() : []
    if (notifications.includes(value)) {
      notifications = notifications.filter(n => n !== value)
    } else {
      notifications = notifications.concat(value)
    }
    this.dataToUpdate.account.notifications = notifications
    this.dataToUpdate.notifications = notifications
  }

  @action getUserData = async (checking = true) => {
    this.checkingLogin = checking
    try {
      const userData = await this.api.request('user', 'get')
      if (typeof userData === 'object') {
        for (let name in this.data) {
          this.data[name] = userData[name]
          this.dataToUpdate[name] = userData[name]
        }
        const {permissions} = userData
        if (typeof permissions === 'object') {
          for (let type in permissions) {
            this.root.permissions.set(permissions[type])
          }
        }
        this.selectedPlan = this.data.billing_plan.id
        if (process.env.SENTRY) {
          Sentry.configureScope(scope => {
            scope.setUser({'email': userData.email})
          })
        }
      }
    } finally {
      this.checkingLogin = false
    }
  }

  @action changeData = async () => {
    this.loading = true
    const body = Object.assign({}, this.dataToUpdate)
    if (body['notifications'] && body['notifications'].indexOf('sms') !== -1) {
      if (!body['phone']) {
        notification['warning']({
          message: 'You should enter your phone number to enable sms notification'
        })
        return
      }
    }
    try {
      const userData = await this.api.request('user', 'patch', body)
      if (typeof userData === 'object') {
        if (userData['email']) {
          for (let name in this.data) {
            this.data[name] = userData[name]
            this.dataToUpdate[name] = userData[name]
          }
          const {permissions} = userData
          if (typeof permissions === 'object') {
            for (let type in permissions) {
              this.root.permissions.set(permissions[type])
            }
          }
          this.selectedPlan = this.data.billing_plan.id
          this.removeErrors()
          notification['warning']({
            message: 'Your changes are saved successfully!'
          })
        } else {
          if (userData['status_code'] === 400 && userData['code'] === '1000') {
            if (userData['field_errors'] && userData['field_errors']['phone']) {
              this.addValidationError({'phone': 'Please enter phone in valid international format +xxxxxxxxxxx'})
            }
          }
        }
      }
    } catch (e) {
      notification['error']({
        message: 'Failed to update your data',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action reset() {
    this.dataToUpdate = mergeDeep({}, this.data)
  }

  @action addValidationError = (errorObj) => {
    this.validationErrors = errorObj
  }

  @action removeErrors = () => {
    this.validationErrors = {}
  }

  @action disableAccount = async (current_password) => {
    const body = {current_password}
    try {
      await this.api.request('user', 'delete', body)
      this.root.auth.logout()
    } catch (e) {
      notification['error']({
        message: 'Failed to disable your account',
        description: e.message || 'Unknown error'
      })
    }
  }

  @action confirmEmail = async (validation_key) => {
    this.emailVerificationFailed = false
    try {
      await this.api.request('user/confirm-email', 'post', {validation_key})
      this.getUserData(false)
    } catch (e) {
      this.emailVerificationFailed = true
      notification['error']({
        message: 'Failed to confirm email',
        description: e.message || 'Unknown error'
      })
    }
  }

  @action resendConfirmation = async () => {
    this.emailVerificationFailed = false
    const {email} = this.data
    try {
      await this.api.request('user/resend-confirmation-letter', 'post', {email})
      this.emailResent = true
    } catch (e) {
      notification['error']({
        message: 'Failed to resend confirmation letter',
        description: e.message || 'Unknown error'
      })
    }
  }

  @action changePlan = async () => {
    this.loading = true
    const body = {plan:this.selectedPlan}
    try {
      await this.api.request('user/billing/subscriptions', 'post', body)
      await this.getUserData(false)
      message.success(`Successfully changed to plan ${this.planSettings[0].name}`)
    } catch (e) {
      if (Array.isArray(e.errors) && e.errors.find(error => error.code == 2002)) {
        notification['warning']({
          message: 'Enter your credit card info to change plan'
        })
      } else {
        notification['error']({
          message: 'Failed to change plan',
          description: e.message || 'Unknown error'
        })
      }
    } finally {
      this.loading = false
    }
  }

  @action hijack = async () => {
    this.loading = true
    const token = localStorage.getItem('refreshToken')
    const body = {
      email: this.hijack_email,
      refresh: token
    }

    try {
      const {access, refresh} = await this.api.request('auth/hijack', 'post', body)
      localStorage.setItem('accessToken', access)
      localStorage.setItem('refreshToken', refresh)
      this.root.redirectTo({pathname:'/manager'})
      await this.getUserData()
    } catch (e) {
      notification['error']({
        message: 'Failed to hijack',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action release = async () => {
    this.loading = true
    try {
      const {access, refresh} = await this.api.request('auth/hijack/release', 'post')
      localStorage.setItem('accessToken', access)
      localStorage.setItem('refreshToken', refresh)
      this.root.redirectTo({pathname:'/manager'})
      await this.getUserData()
    } catch (e) {
      notification['error']({
        message: 'Failed to release',
        description: e.message || 'Unknown error'
      })
    } finally {
      this.loading = false
    }
  }

  @action setDefaults = () => {
    this.data = new Data()
    this.dataToUpdate = new Data()
    this.checkingLogin = false
    this.loading = false
    this.validationErrors = {}
    this.emailVerificationFailed = false
    this.emailResent = false
    this.selectedPlan = null
    this.hijack_email = null
  }
}
